import { type ReactNode, useMemo, useState } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useApiQuery } from '../../hooks/useApiQuery';
import { useModelPrices } from '../../hooks/useModelPrices';
import type { CodeGroupRules } from '../../types/codeRules';
import type { ModelPrice } from '../../types/rateLimits';
import { Chevron } from '../Chevron';
import { FacetSearch } from './FacetSearch';

const modelCompany = (m: ModelPrice) => m.company ?? 'other';
const modelName = (m: ModelPrice) => m.model;

function Mark({ on, label }: { on: boolean; label: string }) {
  return on ? (
    <span className="text-ok" role="img" aria-label={label}>
      ✓
    </span>
  ) : (
    <span
      className="text-muted"
      role="img"
      aria-label={`No ${label.toLowerCase()}`}
    >
      —
    </span>
  );
}

function ModelSearch({ models }: { models: ModelPrice[] }) {
  return (
    <FacetSearch
      title="Models"
      noun="model"
      items={models}
      facet={modelCompany}
      text={modelName}
    >
      {(matches, grouped) => (
        <ul className="mt-1 columns-[26rem] gap-6 text-ink">
          {matches.map(m => (
            <li key={m.model} className="break-all">
              <span className="font-mono">{m.model}</span>
              {/* Proportional, so the separator doesn't take monospace widths. */}
              {m.company && !grouped && (
                <span className="text-muted"> · {m.company}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </FacetSearch>
  );
}

function PrefixList({ prefixes }: { prefixes: string[] }) {
  return (
    <ul className="mt-1 space-y-0.5 text-ink">
      {prefixes.map(prefix => (
        <li key={prefix} className="break-all">
          <span className="font-mono">{prefix}</span>
          {prefix === '*' && (
            <span className="text-muted">
              {' '}
              · every source no other rule matches
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** A group's row, which opens onto what it gives you once there is any. */
function GroupRow({
  group,
  cells,
  count,
  open,
  onToggle,
  children,
}: {
  group: string;
  /** Cells between the name and the count. */
  cells: ReactNode[];
  /** Undefined while loading, or when this page can't list what it gives. */
  count: number | undefined;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <>
      <tr className="border-t border-line align-middle">
        <td className="py-1.5 pr-4 font-mono text-ink">
          {count ? (
            <button
              type="button"
              className="row-action inline-flex items-center gap-1.5 text-left"
              aria-expanded={open}
              onClick={onToggle}
            >
              <Chevron open={open} size={16} />
              {group}
            </button>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <span className="w-4 shrink-0" />
              {group}
            </span>
          )}
        </td>
        {cells.map((cell, i) => (
          <td key={i} className="px-2">
            {cell}
          </td>
        ))}
        <td className="px-2 text-right tabular-nums">
          {count?.toLocaleString()}
        </td>
      </tr>
      {open && !!count && (
        <tr>
          <td colSpan={cells.length + 2} className="pb-3 pl-4">
            {children}
          </td>
        </tr>
      )}
    </>
  );
}

/** What the signed-in user can view, edit and call, as this deployment enforces it. */
export function MyAccessTab() {
  const { user } = useAuthContext();
  const models = useModelPrices();
  const codeRules = useApiQuery<CodeGroupRules[]>(
    '/meta/code-groups',
    !!user?.codeGroups.length
  );
  const [open, setOpen] = useState<string | null>(null);
  const toggle = (group: string) => setOpen(open === group ? null : group);
  const byGroup = useMemo(
    () => Map.groupBy(models.data ?? [], m => m.group),
    [models.data]
  );
  const byCodeGroup = useMemo(
    () => new Map(codeRules.data?.map(g => [g.group, g.prefixes])),
    [codeRules.data]
  );
  if (!user) return null;

  return (
    <div className="space-y-6 p-4 text-xs text-ink-2">
      <section>
        <h2 className="eyebrow">Signed in as</h2>
        <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ink">
          {user.email ?? user.sub}
          {user.isAdmin && <span className="badge">Hawk admin</span>}
        </p>
      </section>

      <section>
        <h2 className="eyebrow">Model groups</h2>
        {user.modelGroups.length === 0 ? (
          <p className="mt-1 text-muted">None.</p>
        ) : (
          <table className="mt-1 w-full max-w-2xl text-left">
            <thead className="eyebrow">
              <tr>
                <th className="py-1.5 pr-4 font-semibold">Group</th>
                <th className="px-2 font-semibold">View results</th>
                <th className="px-2 font-semibold">Edit results</th>
                <th className="px-2 font-semibold">Call models</th>
                <th className="px-2 text-right font-semibold">Models</th>
              </tr>
            </thead>
            <tbody>
              {user.modelGroups.map(g => {
                // Only models you can view are listed, so a group without
                // view access gets no count rather than a misleading 0.
                const listed =
                  g.read && models.data
                    ? (byGroup.get(g.group) ?? [])
                    : undefined;
                return (
                  <GroupRow
                    key={g.group}
                    group={g.group}
                    cells={[
                      <Mark key="read" on={g.read} label="View results" />,
                      <Mark key="write" on={g.write} label="Edit results" />,
                      <Mark key="call" on={g.execute} label="Call models" />,
                    ]}
                    count={listed?.length}
                    open={open === g.group}
                    onToggle={() => toggle(g.group)}
                  >
                    {listed && <ModelSearch models={listed} />}
                  </GroupRow>
                );
              })}
            </tbody>
          </table>
        )}
        {!models.data &&
          (models.error ? (
            <p className="mt-1 text-err">{models.error.message}</p>
          ) : (
            <p className="mt-1 text-muted">Loading models…</p>
          ))}
        <p className="mt-1.5 text-muted">
          Running an eval needs view and call access to every model it uses.
        </p>
      </section>

      <section>
        <h2 className="eyebrow">Code groups</h2>
        {user.codeGroups.length === 0 ? (
          <p className="mt-1 text-muted">
            None. You can read runs whose code no rule restricts.
          </p>
        ) : (
          <table className="mt-1 w-full max-w-2xl text-left">
            <thead className="eyebrow">
              <tr>
                <th className="py-1.5 pr-4 font-semibold">Group</th>
                <th className="px-2 text-right font-semibold">Rules</th>
              </tr>
            </thead>
            <tbody>
              {user.codeGroups.map(group => {
                const prefixes =
                  codeRules.data && (byCodeGroup.get(group) ?? []);
                return (
                  <GroupRow
                    key={group}
                    group={group}
                    cells={[]}
                    count={prefixes?.length}
                    open={open === group}
                    onToggle={() => toggle(group)}
                  >
                    {prefixes && <PrefixList prefixes={prefixes} />}
                  </GroupRow>
                );
              })}
            </tbody>
          </table>
        )}
        {codeRules.error && (
          <p className="mt-1 text-err">
            Couldn’t load their rules: {codeRules.error.message}
          </p>
        )}
      </section>
    </div>
  );
}
